import React, { useMemo } from "react";
import {
  ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis,
  Tooltip, Legend, Area, ComposedChart,
} from "recharts";
import { Card } from "@/components/ui/card";
import { TrendingUp } from "lucide-react";

const COLORS = {
  cash: "#10B981",     // emerald
  credit: "#3B82F6",   // blue
  reversal: "#F59E0B", // amber
};

/**
 * Ledger Trend Chart — visualises Cash, Credit, and Reversal-Reclaim
 * balances over time. Data prop shape:
 *   [{ date: "2026-04-01", cash: 1234, credit: 5678, reversal: 200 }, ...]
 */
export default function LedgerTrendChart({ series = [] }) {
  const rows = useMemo(() =>
    (series || []).map(r => ({
      date: r.date,
      Cash: Number(r.cash || 0),
      Credit: Number(r.credit || 0),
      Reversal: Number(r.reversal || 0),
    })), [series]);

  if (!rows.length) {
    return (
      <Card className="p-6 rounded-sm border text-center text-muted-foreground" data-testid="ledger-chart-empty">
        <TrendingUp className="w-10 h-10 mx-auto opacity-30" />
        <div className="mt-2 text-sm">No ledger transactions in the selected range.</div>
      </Card>
    );
  }

  const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

  return (
    <Card className="p-4 rounded-sm border bg-background" data-testid="ledger-trend-chart">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-600 flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" /> Ledger Trend
          </div>
          <div className="text-sm font-semibold mt-0.5">Cash · Credit · Reversal balances over time</div>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono uppercase text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLORS.cash }} /> Cash</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLORS.credit }} /> Credit</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLORS.reversal }} /> Reversal</span>
        </div>
      </div>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="cashGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={COLORS.cash}   stopOpacity={0.35} />
                <stop offset="100%" stopColor={COLORS.cash}   stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="creditGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={COLORS.credit} stopOpacity={0.30} />
                <stop offset="100%" stopColor={COLORS.credit} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.15)" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 100000 ? `${(v/100000).toFixed(1)}L` : v} />
            <Tooltip formatter={(v) => fmt(v)}
              contentStyle={{ background: "rgba(255,255,255,0.98)", border: "1px solid rgba(0,0,0,0.1)", fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="Cash"     stroke={COLORS.cash}   fill="url(#cashGrad)"   strokeWidth={2} />
            <Area type="monotone" dataKey="Credit"   stroke={COLORS.credit} fill="url(#creditGrad)" strokeWidth={2} />
            <Line type="monotone" dataKey="Reversal" stroke={COLORS.reversal} strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}


/**
 * Small utility that reshapes a WhiteBooks ledger response into the trend
 * series format. Handles common shapes: `txn` list with dt/cr/dr, `data.txn`,
 * or a plain array. Falls back to opening + running balance if closing balance
 * isn't present. Non-ledger data → empty array (caller shows empty state).
 */
export function normaliseLedgerToSeries(raw, kind = "cash") {
  if (!raw) return [];
  const root = raw?.data?.data || raw?.data || raw;
  const txns = root?.txn || root?.tran || root?.transactions || (Array.isArray(root) ? root : []);
  if (!Array.isArray(txns) || !txns.length) return [];
  let running = Number(root?.op_bal || root?.opening_balance || 0);
  const out = [];
  for (const t of txns) {
    const date = t.dt || t.date || t.tran_date || t.txn_dt || "";
    const cr = Number(t.cr || t.credit || 0);
    const dr = Number(t.dr || t.debit  || 0);
    running += cr - dr;
    out.push({ date, [kind]: running });
  }
  return out;
}


/**
 * Merge multiple ledger series (from cash/credit/reversal responses) into
 * one row-per-date frame. Missing dates back-fill with prior value.
 */
export function mergeLedgerSeries({ cash = [], credit = [], reversal = [] }) {
  const dates = Array.from(new Set([
    ...cash.map(r => r.date),
    ...credit.map(r => r.date),
    ...reversal.map(r => r.date),
  ])).filter(Boolean).sort();
  const idx = (arr) => Object.fromEntries(arr.map(r => [r.date, r]));
  const iCash = idx(cash), iCred = idx(credit), iRev = idx(reversal);
  let lastC = 0, lastCr = 0, lastR = 0;
  return dates.map(d => {
    lastC  = iCash[d]?.cash     ?? lastC;
    lastCr = iCred[d]?.credit   ?? lastCr;
    lastR  = iRev[d]?.reversal  ?? lastR;
    return { date: d, cash: lastC, credit: lastCr, reversal: lastR };
  });
}
